import AdvanceTable from "components/common/advance-table/AdvanceTable";
import AdvanceTableSearchBox from "components/common/advance-table/AdvanceTableSearchBox";
import CardDropdown from "components/common/CardDropdown";
import CustomPaginationFooter from "components/common/CustomPaginationFooter";
import SubtleBadge from "components/common/SubtleBadge";
import { useAdvanceTable } from "hooks";
import useDonorDonations from "hooks/donations/useDonorDonations";
import AdvanceTableProvider from "providers/AdvanceTableProvider";
import React, { useEffect, useState } from "react";
import { Button, Card, Col, Dropdown, Row, Spinner } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import WidgetSectionTitle from "widgets/WidgetSectionTitle";

export const DonorDonations = () => {
  const authToken = sessionStorage.getItem("authToken");
  const navigate = useNavigate();
  const { donorId } = useParams();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const { donations, loading, totalPages, totalElements, error } =
    useDonorDonations(authToken, donorId, page, pageSize);

  const { id, firstName, lastName, email, phone, profilePhoto, gender } =
    donations[0]?.donor || {};

  const formatID = (id) => `${id.slice(0, 8)}...`;
  const formatText = (text) => `${text.slice(0, 20)}...`;

  const columns = [
    {
      accessorKey: "id",
      header: "ID",
      cell: ({ getValue }) => formatID(getValue()),
      meta: {
        headerProps: { className: "text-900" },
      },
    },
    {
      accessorKey: "donor",
      header: "Donor",
      cell: ({ getValue }) => {
        const donor = getValue();
        if (!donor) return "NULL";

        const fullName = `${donor.firstName || ""} ${
          donor.lastName || ""
        }`.trim();
        return fullName || donor.nickName || "NULL";
      },
      meta: {
        headerProps: { className: "text-900" },
      },
    },
    {
      accessorKey: "doneeUser",
      header: "Donee",
      cell: ({ getValue }) => {
        const donee = getValue();
        if (!donee) return "NULL";
        const fullName = `${donee.firstName || ""} ${
          donee.lastName || ""
        }`.trim();
        return fullName || donee.nickName || "NULL";
      },
      meta: {
        headerProps: { className: "text-900" },
      },
    },
    {
      accessorKey: "amount",
      header: "Amount (USD)",
      cell: ({ getValue }) =>
        getValue() ? `$${getValue().toFixed(2)}` : "$0.00",
      meta: {
        headerProps: { className: "text-900" },
      },
    },
    {
      accessorKey: "balance",
      header: "Balance (USD)",
      cell: ({ getValue }) =>
        getValue() ? `$${getValue().toFixed(2)}` : "$0.00",
      meta: {
        headerProps: { className: "text-900" },
      },
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ getValue }) => {
        const value = getValue();

        const statusMap = {
          ACTIVE: { type: "success", label: "Active" },
          PENDING: { type: "warning", label: "Pending" },
          EXPIRED: { type: "danger", label: "Expired" },
          DELETED: { type: "danger", label: "Deleted" },
          NEW: { type: "info", label: "New" },
        };

        const { type, label } = statusMap[value] || {
          type: "secondary",
          label: "Unknown",
        };
        return (
          <SubtleBadge pill bg={type}>
            {label}
          </SubtleBadge>
        );
      },
      meta: {
        headerProps: { className: "text-900" },
        cellProps: { className: "fs-9" },
      },
    },
    {
      accessorKey: "purposes",
      header: "Purposes",
      cell: ({ getValue }) => {
        const purposes = getValue();
        if (!purposes || purposes.length === 0) return "Not provided";
        return formatText(purposes.map((p) => p.name).join(", "));
      },
      meta: {
        headerProps: { className: "text-900" },
      },
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => {
        const item = row.original;
        return (
          <CardDropdown drop="start">
            <div className="py-2">
              <Dropdown.Item
                onClick={() => navigate(`/olitask/donations/${item.id}`)}
              >
                View details
              </Dropdown.Item>
              {item.balance !== 0 && (
                <Dropdown.Item
                  onClick={() =>
                    navigate(`/olitask/donations/${item.id}/issue-credit`)
                  }
                >
                  Issue Credit
                </Dropdown.Item>
              )}
              <Dropdown.Divider as="div" />
              <Dropdown.Item onClick={() => {}} className="text-danger">
                Delete
              </Dropdown.Item>
            </div>
          </CardDropdown>
        );
      },
      meta: {
        headerProps: { className: "text-900 text-center" },
        cellProps: { className: "text-center" },
      },
    },
  ];

  const table = useAdvanceTable({
    data: donations,
    columns,
    sortable: true,
    pagination: true,
    perPage: 10,
  });

  if (loading && donations.length === 0) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: 400 }}
      >
        <Spinner animation="border" variant="success" />
        <span className="ms-3" style={{ fontSize: "13.33px" }}>
          Loading...
        </span>
      </div>
    );
  }

  return (
    <>
      <Card className="mb-4 shadow-none mt-3 rounded border-1">
        <Card.Body className="d-flex align-items-center py-3 px-4">
          {/* Left side: Back button */}
          <div className="d-flex align-items-center" style={{ minWidth: 90 }}>
            <Button
              type="button"
              onClick={() => navigate("/olitask/donations/list")}
              size="sm"
              aria-label="Go back"
              variant="outline-success"
            >
              <span style={{ fontSize: "1.3em", lineHeight: 1 }}>←</span> Back
            </Button>
          </div>

          {/* Center: Title */}
          <div className="px-3">
            <h4 className="mb-0 fw-semibold text-base">Donor donations</h4>
          </div>
        </Card.Body>
      </Card>

      <AdvanceTableProvider {...table}>
        <AdvanceTable
          headerClassName="bg-200 text-nowrap align-middle"
          rowClassName="btn-reveal-trigger text-nowrap align-middle"
          tableProps={{
            bordered: true,
            striped: true,
            className: "fs-10 mb-0 overflow-hidden",
          }}
        />

        <div className="mt-3">
          <CustomPaginationFooter
            page={page}
            totalPages={totalPages}
            totalElements={totalElements}
            loading={loading}
            onPrev={() => setPage((prev) => Math.max(prev - 1, 0))}
            onNext={() => setPage((prev) => Math.min(prev + 1, totalPages - 1))}
          />
        </div>
      </AdvanceTableProvider>
    </>
  );
};
